import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Badge, Button, Dropdown, Menu, Modal, Switch, Tooltip, message } from 'antd';
import ReceptionMinuteSelect, { useGetHourLabel } from 'components/ReceptionMinuteSelect';
import StatusButton from 'components/StatusButton';
import useCrud from 'hooks/useCrud';
import useModal from 'hooks/useModal';
import dayjs from 'lib/dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { putTalkAiReplyConf } from 'services/api/conversation.ai.reply.setting';
import { getReceptionPriority } from 'services/api/im.setting';
import {
  StyledRobotIcon,
  Timer,
  Wrapper,
} from 'views/Conversation/components/AiReceptionForm/AiReceptionTimer';
import { What } from 'views/Marketing/components/PolicyTable';
import { Container, modalClassName } from '../MessageScenario/ScenarioModal';
import PermissionTips from '../MessageScenario/PermissionTips';
import { permissionNameIdMap, useGetPermission } from 'hooks/business/useNewPermisson';
import { Task } from 'views/Aide/Basis/Pause/Task';
import styled from '@emotion/styled';
import { cancelScheduleIdle, scheduleIdle } from 'utils/scheduleIdle';
import { useMakeConversationCache } from 'hooks/business/conversation';

export interface PauseAutoReplyProps {
  conversation: Conversation.Conversation;
}

function PauseAutoReply(props: PauseAutoReplyProps) {
  const { conversation } = props;

  const {
    conversationId,
    channel: platform,
    region,
    storeId,
    pauseAiReply = false,
    pauseAiReplyEndMillis,
  } = conversation;

  const setConversation = useMakeConversationCache('set');
  const { t, getHourLabel } = useGetHourLabel();

  const { loading, item, setItem, fetch } = useCrud({
    read: getReceptionPriority,
  });

  const [timer, setTimer] = useState<string>();

  const timeoutRef = useRef<NodeJS.Timeout>(-1 as any);

  const refsCurrent = {
    setItem,
    fetch,
    t,
    changePauseAiReply: (partial: Partial<Conversation.Conversation>) =>
      setConversation({
        conversationId,
        ...partial,
      } as Conversation.Conversation),
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const idleCallback = scheduleIdle(() => {
      refs.current.fetch({ storeId }).catch(() => {
        message.error(refs.current.t('common.feedback.fetchFailed'));
      });
    });

    return () => {
      refs.current.setItem(undefined);
      if (idleCallback) {
        cancelScheduleIdle(idleCallback);
      }
    };
  }, [storeId]);

  useEffect(() => {
    const endTime = parseInt(pauseAiReplyEndMillis || '0', 10);

    if (!pauseAiReply) {
      timeoutRef.current !== undefined && clearInterval(timeoutRef.current);
      setTimer('');
    } else if (endTime) {
      const endMoment = dayjs(endTime);

      const countdown = () => {
        const diff = endMoment.diff(dayjs());

        if (diff <= 0) {
          clearInterval(timeoutRef.current);
          setTimer('');
          refs.current.changePauseAiReply({
            pauseAiReply: false,
            pauseAiReplyEndMillis: '0',
          });
        } else {
          const hours = Math.floor(diff / 60 / 60 / 1000);
          const timer = `${hours >= 10 ? hours : `0${hours}`}:${dayjs
            .duration(diff)
            .format('mm:ss')}`;
          setTimer(timer);
        }
      };

      countdown();

      timeoutRef.current = setInterval(countdown, 1000);
    }

    return () => clearInterval(timeoutRef.current);
  }, [pauseAiReply, pauseAiReplyEndMillis]);

  const { visible, show, hide, meta } = useModal<{
    pause: boolean;
    pauseMinute: number;
    resetPauseMinute: boolean;
  }>();

  const {
    visible: taskVisible,
    show: showTask,
    hide: hideTask,
    meta: taskMeta,
  } = useModal<string | number>();

  const getPermission = useGetPermission();

  const permission = useMemo(
    () =>
      getPermission('配置暂停自动回复', {
        platform,
        region,
        storeId: Number(storeId),
      }),
    [getPermission, platform, region, storeId],
  );

  if (!item) {
    return loading ? <LoadingOutlined /> : null;
  }

  const { humanFirst, pauseAi, pauseMinute, pauseAutoReplyGroupId } = item;

  const icon = (
    <Wrapper
      mode={timer ? 'timer' : 'normal'}
      onClick={
        !pauseAi
          ? () =>
              show({
                pause: pauseAiReply,
                pauseMinute,
                resetPauseMinute: false,
              })
          : undefined
      }
    >
      {timer ? (
        <>
          <StyledRobotIcon color="#464D5A" status="paused" />
          <Timer>{timer}</Timer>
        </>
      ) : (
        <>
          <StyledRobotIcon color="#464D5A" status={humanFirst ? 'paused' : 'working'} />
        </>
      )}
    </Wrapper>
  );

  const menu = (
    <Dropdown
      trigger={['hover']}
      overlay={
        <Menu
          onClick={({ key }) => {
            if (key === 'config') {
              show({
                pause: pauseAiReply,
                pauseMinute,
                resetPauseMinute: false,
              });
            } else if (key === 'pause' || key === 'reset') {
              putTalkAiReplyConf({
                conversationId,
                enable: key === 'pause',
                minutes: pauseMinute,
                reason: 'MARK',
              })
                .then((res) => {
                  refs.current.changePauseAiReply(res.data.data);

                  message.success(t('common.feedback.saveSuccess'));

                  hide();
                })
                .catch(() => {
                  message.error(t('common.feedback.saveFailed'));
                });
            }
          }}
        >
          {pauseAiReply ? (
            <Menu.Item key="reset">
              {t('scene.chatWorkspace.currentConversation.pauseAutoReply.action.resume')}
            </Menu.Item>
          ) : (
            <Menu.Item key="pause">
              {t('scene.chatWorkspace.currentConversation.pauseAutoReply.pauseForTime', {
                time: getHourLabel(pauseMinute / 60).label,
              })}
            </Menu.Item>
          )}
          <Menu.Item key="config">
            {t('scene.chatWorkspace.currentConversation.pauseAutoReply.title')}
          </Menu.Item>
        </Menu>
      }
    >
      {icon}
    </Dropdown>
  );

  const modal = meta && (
    <Modal
      width={420}
      title={t('scene.chatWorkspace.currentConversation.pauseAutoReply.mode.aiAutoReply')}
      visible={visible}
      onCancel={() => hide()}
      footer={
        <Footer>
          {pauseAutoReplyGroupId ? (
            <Button type="text" onClick={() => showTask(pauseAutoReplyGroupId)}>
              {t('scene.chatWorkspace.currentConversation.pauseAutoReply.action.viewConfig')}
            </Button>
          ) : (
            <div />
          )}
          <div>
            <Button onClick={() => hide()}>{t('common.action.cancel')}</Button>
            <StatusButton
              type="primary"
              disabled={meta.pause === pauseAiReply && !meta.resetPauseMinute}
              onClick={() => {
                const { pause, pauseMinute } = meta;

                return putTalkAiReplyConf({
                  conversationId,
                  enable: pause,
                  minutes: pauseMinute,
                  reason: 'MARK',
                })
                  .then((res) => {
                    refs.current.changePauseAiReply(res.data.data);

                    message.success(t('common.feedback.saveSuccess'));

                    hide();
                  })
                  .catch(() => {
                    message.error(t('common.feedback.saveFailed'));
                  });
              }}
            >
              {t('ViewDashboard.确认')}
            </StatusButton>
          </div>
        </Footer>
      }
    >
      <Config>
        <dt>
          {t('scene.chatWorkspace.currentConversation.pauseAutoReply.mode.agentPriority')}
          <Tooltip
            title={t(
              'scene.chatWorkspace.currentConversation.pauseAutoReply.mode.agentPriority.description',
            )}
          >
            <What />
          </Tooltip>
          ：
        </dt>
        <dd>
          {humanFirst ? (
            <Badge status="success" text={t('common.status.enabled')} />
          ) : (
            <div>
              <Badge status="default" text={t('common.status.disabled')} />
              <Tooltip
                title={t(
                  'scene.chatWorkspace.currentConversation.pauseAutoReply.feedback.agentPriorityNotEnabled',
                )}
              >
                <InfoCircleOutlined />
              </Tooltip>
            </div>
          )}
        </dd>
      </Config>
      <Config>
        <dt>{t('scene.chatWorkspace.currentConversation.pauseAutoReply.mode.manualPause')}：</dt>
        <dd>
          {!pauseAi && !meta.pause ? (
            <Tooltip
              title={t(
                'scene.chatWorkspace.currentConversation.pauseAutoReply.feedback.manualPauseNotEnabled',
              )}
            >
              <Switch
                disabled
                checkedChildren={t('common.status.enabled')}
                unCheckedChildren={t('common.status.disabled')}
                checked={meta.pause}
              />
            </Tooltip>
          ) : (
            <Switch
              checkedChildren={t('common.status.enabled')}
              unCheckedChildren={t('common.status.disabled')}
              checked={meta.pause}
              onChange={(pause) => {
                show({
                  ...meta,
                  pause,
                  resetPauseMinute: pause && !timer ? true : false,
                });
              }}
            />
          )}
          {meta.pause &&
            (meta.resetPauseMinute ? (
              <div>
                {t('scene.chatWorkspace.currentConversation.pauseAutoReply.action.selectPauseTime')}
                <ReceptionMinuteSelect
                  dropdownMatchSelectWidth={false}
                  value={meta.pauseMinute}
                  onChange={(pauseMinute) => show({ ...meta, pauseMinute })}
                />
              </div>
            ) : (
              <div>
                {t('scene.chatWorkspace.currentConversation.pauseAutoReply.resumeAfterTime', {
                  x: timer,
                })}
                {pauseAi && (
                  <Button type="link" onClick={() => show({ ...meta, resetPauseMinute: true })}>
                    {t(
                      'scene.chatWorkspace.currentConversation.pauseAutoReply.action.resetPauseTime',
                    )}
                  </Button>
                )}
              </div>
            ))}
        </dd>
      </Config>
    </Modal>
  );

  const taskModal = taskMeta && (
    <Modal
      title={t('scene.chatWorkspace.currentConversation.pauseAutoReply.action.pause')}
      width="80vw"
      visible={taskVisible}
      onCancel={() => hideTask()}
      footer={false}
      className={modalClassName}
    >
      <PermissionTips
        permission={permission}
        permissionId={permissionNameIdMap['配置暂停自动回复']}
        storeId={storeId}
      />
      <Container>
        <Task
          readonly={!permission}
          routeMode={false}
          id={taskMeta}
          onModeUpdate={(partial) => {
            setItem((pre) => pre && { ...pre, ...partial });

            if (pauseAi && partial.pauseAi === false && meta) {
              show({
                ...meta,
                pause: pauseAiReply ? meta.pause : false,
                resetPauseMinute: false,
              });
            }
          }}
        />
      </Container>
    </Modal>
  );

  return (
    <>
      {!pauseAi ? icon : menu}
      {modal}
      {taskModal}
    </>
  );
}

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    align-items: center;
  }
`;

const Config = styled.dl`
  > dt {
    margin-bottom: 8px;
    > .anticon {
      margin: 0 4px;
    }
  }
  > dd {
    > div > .anticon {
      margin-left: 8px;
      cursor: pointer;
    }
    > button + div,
    > span + div {
      display: flex;
      align-items: center;
      height: 48px;
      padding: 0 14px;
      margin-top: 12px;
      background: #f7f9fa;
    }
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

export default PauseAutoReply;
